import { PublicConfig, PublicConfigSchema, normalizeApiBaseUrl } from './public-config';

/**
 * Frontend helper: fetch /api/config/public, validate against the
 * shared schema, cache for a short TTL in localStorage so reloads
 * don't re-hit the network.
 *
 * Apps call `loadPublicConfig()` once at boot (typically from
 * src/lib/config.ts) and synchronously read `getCachedPublicConfig()`
 * thereafter. The single allowed bootstrap env value remains the
 * `apiBaseUrl` argument — every other formerly-`VITE_*` value comes
 * from the response.
 */

const STORAGE_KEY = 'rynora-public-config';
const STORAGE_TS_KEY = 'rynora-public-config-ts';
const DEFAULT_TTL_MS = 5 * 60_000;

let inMemory: PublicConfig | null = null;

function readCache(ttlMs: number): PublicConfig | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const tsRaw = window.localStorage.getItem(STORAGE_TS_KEY);
    if (!raw || !tsRaw) return null;
    const ts = parseInt(tsRaw, 10);
    if (!Number.isFinite(ts)) return null;
    if (Date.now() - ts > ttlMs) return null;
    const parsed = PublicConfigSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeCache(value: PublicConfig): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    window.localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
  } catch {
    // Safari private mode, sandboxed iframes — ignore.
  }
}

export interface LoadPublicConfigOptions {
  apiBaseUrl: string;
  /** Override the default 5-minute cache TTL. */
  ttlMs?: number;
  /** Force a network fetch even if a fresh cache exists. */
  refresh?: boolean;
}

/**
 * Fetch the public config and cache it. Call once at app boot.
 * Returns the cached payload immediately if fresh; otherwise issues
 * a network request and caches the result.
 */
export async function loadPublicConfig(opts: LoadPublicConfigOptions): Promise<PublicConfig> {
  const ttl = opts.ttlMs ?? DEFAULT_TTL_MS;
  if (!opts.refresh) {
    const cached = readCache(ttl);
    if (cached) {
      inMemory = cached;
      return cached;
    }
  }
  const res = await fetch(`${opts.apiBaseUrl}/api/config/public`);
  if (!res.ok) {
    throw new Error(`Failed to load public config (HTTP ${res.status}).`);
  }
  const json = await res.json();
  const parsed = PublicConfigSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(
      `Public config payload did not match schema. First issue: ${parsed.error.issues[0]?.message ?? 'unknown'}.`,
    );
  }
  // Defend against a mis-set admin_config.connection.apiBaseUrl that
  // already ends in `/api` — SPA callers append `/api/...` themselves.
  const data: PublicConfig = {
    ...parsed.data,
    connection: {
      ...parsed.data.connection,
      apiBaseUrl: normalizeApiBaseUrl(parsed.data.connection.apiBaseUrl),
    },
  };
  inMemory = data;
  writeCache(data);
  return data;
}

/**
 * Synchronously read the in-memory or cached public config. Returns
 * null if `loadPublicConfig` hasn't been awaited yet AND the cache is
 * empty/stale. Apps should ensure `loadPublicConfig` finishes before
 * rendering anything that depends on the config.
 */
export function getCachedPublicConfig(): PublicConfig | null {
  if (inMemory) return inMemory;
  return readCache(DEFAULT_TTL_MS);
}

/**
 * Clear the cache; the next `loadPublicConfig` will hit the network.
 */
export function invalidatePublicConfig(): void {
  inMemory = null;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_TS_KEY);
  } catch {
    // ignore
  }
}
